import { useState } from 'react';
import useLocalStorage from '@/hooks/useLocalStorage';
import { defaultOrders, type AdminOrder } from '@/mocks/adminData';

const statusOptions = ['novo', 'em-atendimento', 'finalizado', 'cancelado'] as const;
const statusLabels: Record<string, string> = {
  'novo': 'Novo',
  'em-atendimento': 'Em atendimento',
  'finalizado': 'Finalizado',
  'cancelado': 'Cancelado',
};
const statusClasses: Record<string, string> = {
  'novo': 'bg-blue-100 text-blue-700',
  'em-atendimento': 'bg-amber-100 text-amber-700',
  'finalizado': 'bg-green-100 text-green-700',
  'cancelado': 'bg-red-100 text-red-700',
};

function OrdersPage() {
  const [orders, setOrders] = useLocalStorage<AdminOrder[]>('admin_orders', defaultOrders);
  const [detailOrder, setDetailOrder] = useState<AdminOrder | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminOrder | null>(null);
  const [filterStatus, setFilterStatus] = useState('todos');

  const filtered = filterStatus === 'todos' ? orders : orders.filter((o) => o.status === filterStatus);

  const handleStatusChange = (id: string, status: AdminOrder['status']) => {
    setOrders(orders.map((o) => (o.id === id ? { ...o, status } : o)));
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    setOrders(orders.filter((o) => o.id !== deleteTarget.id));
    setDeleteTarget(null);
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">Pedidos recebidos</h1>
          <p className="text-sm text-foreground-500 mt-1">Visualize os pedidos enviados pelo carrinho.</p>
        </div>
      </div>

      {/* Status Filter */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {['todos', ...statusOptions].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setFilterStatus(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
              filterStatus === s ? 'bg-primary-600 text-white' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
            }`}
          >
            {s === 'todos' ? 'Todos' : statusLabels[s]}
          </button>
        ))}
        <span className="text-xs text-foreground-500 ml-2">{filtered.length} pedido(s)</span>
      </div>

      <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
              <i className="ri-inbox-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm text-foreground-500">Nenhum pedido encontrado.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-200 bg-background-50">
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500">Pedido</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500">Cliente</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 hidden sm:table-cell">Data</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 hidden md:table-cell">Produtos</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500">Total</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-foreground-500">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {filtered.map((order) => (
                  <tr key={order.id} className="hover:bg-background-50 transition-colors">
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono text-foreground-500">{order.id}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-foreground-800">{order.customerName}</span>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className="text-xs text-foreground-500">{order.date}</span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-xs text-foreground-600">{order.products.length} produto(s)</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-semibold text-foreground-800">R$ {order.total.toFixed(2)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={order.status}
                        onChange={(e) => handleStatusChange(order.id, e.target.value as AdminOrder['status'])}
                        className={`text-xs px-2 py-1 rounded-full font-medium border-0 ${statusClasses[order.status] || ''} cursor-pointer`}
                      >
                        {statusOptions.map((s) => (
                          <option key={s} value={s}>{statusLabels[s]}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button type="button" onClick={() => setDetailOrder(order)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200 text-foreground-500 hover:text-foreground-700 transition-colors">
                          <i className="ri-eye-line text-sm" />
                        </button>
                        <button type="button" onClick={() => setDeleteTarget(order)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-foreground-400 hover:text-red-500 transition-colors">
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {detailOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDetailOrder(null)} />
          <div className="relative bg-white rounded-xl border border-background-200 w-full max-w-md max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-semibold text-foreground-900">Detalhes do pedido</h2>
              <button type="button" onClick={() => setDetailOrder(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200 text-foreground-500">
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="space-y-3 mb-5">
              <div className="flex justify-between text-sm"><span className="text-foreground-500">Pedido</span><span className="font-mono text-foreground-800">{detailOrder.id}</span></div>
              <div className="flex justify-between text-sm"><span className="text-foreground-500">Cliente</span><span className="text-foreground-800">{detailOrder.customerName}</span></div>
              <div className="flex justify-between text-sm"><span className="text-foreground-500">Data</span><span className="text-foreground-800">{detailOrder.date}</span></div>
              <div className="flex justify-between text-sm"><span className="text-foreground-500">Status</span><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusClasses[detailOrder.status]}`}>{statusLabels[detailOrder.status]}</span></div>
            </div>
            <h3 className="text-sm font-semibold text-foreground-800 mb-2">Produtos</h3>
            <div className="space-y-2 mb-4">
              {detailOrder.products.map((p, i) => (
                <div key={i} className="flex justify-between text-sm py-2 border-b border-background-100 last:border-0">
                  <div>
                    <span className="text-foreground-800">{p.name}</span>
                    <span className="text-xs text-foreground-500 ml-2">Tam: {p.size} x{p.quantity}</span>
                  </div>
                  <span className="text-foreground-700 font-medium">R$ {(p.price * p.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-between text-base font-semibold pt-3 border-t border-background-200">
              <span className="text-foreground-900">Total</span>
              <span className="text-primary-700">R$ {detailOrder.total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteTarget(null)} />
          <div className="relative bg-white rounded-xl border border-background-200 w-full max-w-sm p-6">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3"><i className="ri-delete-bin-line text-lg text-red-500" /></div>
            <p className="text-sm text-foreground-700 text-center mb-1">Tem certeza que deseja excluir?</p>
            <p className="text-xs text-foreground-500 text-center mb-5">{deleteTarget.id} — {deleteTarget.customerName}</p>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setDeleteTarget(null)} className="flex-1 px-4 py-2.5 rounded-full text-sm font-medium text-foreground-600 bg-background-100 hover:bg-background-200 transition-colors whitespace-nowrap">Cancelar</button>
              <button type="button" onClick={handleDelete} className="flex-1 px-4 py-2.5 rounded-full text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors whitespace-nowrap">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrdersPage;